/**
 * ADR-0013 — Customer balance deltas from sales (Créances).
 *
 * Nur bei explizitem Kredit-/Restbetrag — POS-Cash-Unterzahlung bleibt Validation-Fehler,
 * kein stiller Credit.
 *
 * Cap-Basis = getSaleTotalAmount (void-/cancel-aware), damit Paid und Unpaid Dualen
 * desselben Totals sind.
 */

import { getSaleTotalAmount } from '../../restaurant/restaurantDishSaleService';

function lookupRemainingDebt(customerDebts, customerId) {
  if (customerId == null || customerId === '') return 0;
  const debt = (customerDebts || []).find(
    (d) => d && String(d.customerId) === String(customerId)
  );
  return Math.max(0, Number(debt?.remainingDebt) || 0);
}

/**
 * @param {object} [sale]
 * @returns {number}
 */
export function resolveSaleUnpaidAmount(sale) {
  if (!sale) return 0;
  const total = getSaleTotalAmount(sale);
  if (total <= 0) return 0;

  const explicit = Number(
    sale.unpaidAmount ?? sale.creditAmount ?? sale.remainingDebt ?? NaN
  );
  if (Number.isFinite(explicit) && explicit > 0) {
    return Math.min(explicit, total);
  }

  const method = String(sale.paymentMethod || sale.paymentType || '').toLowerCase();
  if (method === 'credit' || method === 'credit_payment' || method === 'on_account') {
    return total;
  }

  return 0;
}

/**
 * @param {object} sale
 * @param {Array} [customerDebts]
 * @returns {null|{ customerId: *, customerName: string, delta: number, previousBalance: number, nextBalance: number, saleId: * }}
 */
export function planCustomerBalanceDeltaFromSale(sale, customerDebts = []) {
  const customerId = sale?.customerId;
  const delta = resolveSaleUnpaidAmount(sale);
  if (!customerId || !(delta > 0)) return null;

  const previousBalance = lookupRemainingDebt(customerDebts, customerId);
  return {
    customerId,
    customerName: sale.customerName || '',
    delta,
    previousBalance,
    nextBalance: previousBalance + delta,
    saleId: sale.id || sale.orderId || null
  };
}

/**
 * Mutiert parsed.customerDebts. delta > 0 erhöht Créance; delta < 0 (Storno) senkt sie.
 * @param {object} parsed
 * @param {{ customerId: *, customerName?: string, delta: number, saleId?: * }} plan
 */
export function applyCustomerBalanceDelta(parsed, plan) {
  if (!parsed || !plan?.customerId || !Number.isFinite(plan.delta) || plan.delta === 0) {
    return null;
  }

  const debts = Array.isArray(parsed.customerDebts) ? [...parsed.customerDebts] : [];
  const idx = debts.findIndex(
    (d) => d && String(d.customerId) === String(plan.customerId)
  );
  const now = new Date().toISOString();
  const delta = Number(plan.delta);

  if (idx >= 0) {
    const prev = debts[idx];
    const remainingDebt = Math.max(0, (Number(prev.remainingDebt) || 0) + delta);
    const totalDebt =
      delta > 0
        ? Math.max(0, (Number(prev.totalDebt) || 0) + delta)
        : Math.max(remainingDebt, (Number(prev.totalDebt) || 0) + delta);
    debts[idx] = {
      ...prev,
      remainingDebt,
      totalDebt: Math.max(0, totalDebt),
      creditUsed: remainingDebt,
      creditAvailable: Math.max(
        0,
        (Number(prev.creditLimit) || 0) - remainingDebt
      ),
      updatedAt: now
    };
  } else if (delta > 0) {
    debts.push({
      // Stable PK: one ledger row per customer (matches Hybrid cloud id)
      id: String(plan.customerId),
      customerId: plan.customerId,
      customerName: plan.customerName || '',
      totalDebt: delta,
      paidAmount: 0,
      remainingDebt: delta,
      creditLimit: 0,
      creditUsed: delta,
      creditAvailable: 0,
      invoices: [],
      createdAt: now,
      updatedAt: now
    });
  } else {
    return null;
  }

  parsed.customerDebts = debts;
  return { ...plan, applied: true, at: now };
}
